import { createContext, useContext, useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to ensure user records exist (called when user becomes authenticated)
  const ensureUserRecords = async (userId, userMetadata) => {
    try {
      // Check if subscription exists
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (!existingSub) {
        await supabase.from('subscriptions').insert({
          user_id: userId,
          status: 'free',
          plan: 'free'
        });
      }

      // Check if user_preferences exists
      const { data: existingPrefs } = await supabase
        .from('user_preferences')
        .select('id, first_name, last_name')
        .eq('user_id', userId)
        .single();

      // Extract name from metadata - Google OAuth provides full_name/name,
      // regular signup uses first_name/last_name, Google profile uses given_name/family_name
      let firstName = userMetadata?.first_name || userMetadata?.given_name || '';
      let lastName = userMetadata?.last_name || userMetadata?.family_name || '';

      // Fallback: split full_name or name (Google OAuth typically provides these)
      if (!firstName && (userMetadata?.full_name || userMetadata?.name)) {
        const fullName = userMetadata.full_name || userMetadata.name;
        const parts = fullName.trim().split(/\s+/);
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }

      if (!existingPrefs) {
        await supabase.from('user_preferences').insert({
          user_id: userId,
          email_opt_in: userMetadata?.email_opt_in || false,
          first_name: firstName,
          last_name: lastName
        });
      } else if (firstName && !existingPrefs.first_name) {
        // Row exists but name is empty (e.g., Google OAuth name wasn't captured initially)
        // Only update last_name if it's also empty, to avoid overwriting user-entered values
        const updateData = { first_name: firstName };
        if (lastName && !existingPrefs.last_name) {
          updateData.last_name = lastName;
        }
        await supabase.from('user_preferences')
          .update(updateData)
          .eq('user_id', userId);
      }
    } catch (err) {
      console.error('Error ensuring user records:', err);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      // Ensure records exist for authenticated user (non-blocking)
      if (session?.user) {
        ensureUserRecords(session.user.id, session.user.user_metadata);
        // Identify user in PostHog on initial load (no PII)
        posthog.identify(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      // Ensure records exist when user signs in (including via email verification link)
      if (session?.user) {
        ensureUserRecords(session.user.id, session.user.user_metadata);
        // Identify user in PostHog (no PII)
        posthog.identify(session.user.id);
      } else {
        // Reset PostHog on logout
        posthog.reset();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, emailOptIn = false, firstName = '', lastName = '') => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}`,
        data: {
          first_name: firstName,
          last_name: lastName,
          email_opt_in: emailOptIn
        }
      }
    });

    if (error) throw error;

    // Note: subscription and user_preferences records are created when user becomes authenticated
    // (via onAuthStateChange or getSession) because RLS requires the user to be fully authenticated

    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // Records will be ensured via onAuthStateChange listener
    setUser(data.user);
    return data;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // If the error is about missing session, just clear the local user state
        if (error.message?.includes('session') || error.message?.includes('Session')) {
          setUser(null);
          return;
        }
        throw error;
      }
      setUser(null);
    } catch (error) {
      // Even if there's an error, clear the local user state
      setUser(null);
    }
  };

  const resetPassword = async (email) => {
    // Use window.location.origin to get the current domain
    const redirectUrl = `${window.location.origin}/reset-password`;

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });

    if (error) {
      console.error('Password reset error:', error);
      throw error;
    }

    return data;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}`
      }
    });

    if (error) throw error;
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, signUp, signIn, signOut, resetPassword, signInWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
};